/**
 * Created: 2026-10-29
 * Initial commit: Port event start / score entry / completion from mini to web admin
 *
 * Match plan of a started event: lets the organizer enter set scores per match and
 * complete the event once every match has a score.
 */

import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { message } from '@homemaster/jslib'
import AddIcon from '@mui/icons-material/Add'
import { Box, Button, Tab, Tabs, Typography } from '@mui/material'
import {
  buildMatch,
  type Event,
  EventPlanner,
  expandCompetitors,
  groupConsecutiveByKey,
  type Match,
  type MatchSide,
} from '@tc-app/shared/event'
import { useState } from 'react'
import {
  CompleteEventDocument,
  RequestErrorCode,
  UpdateEventScoreDocument,
} from '../gql/graphql'
import SetScoreDialog, { type SetScore } from './SetScoreDialog'
import UserAvatar from './UserAvatar'

gql`
  mutation UpdateEventScore(
    $score: SetScoreInput!
    $matchId: UUID
    $newMatch: NewMatchInput
  ) {
    updateEventScore(score: $score, matchId: $matchId, newMatch: $newMatch) {
      id
      scoreUpdatedAt
    }
  }

  mutation CompleteEvent(
    $eventId: UUID!
    $rankInput: [PlayerRankInput!]!
    $scoreUpdatedAt: DateTime!
  ) {
    completeEvent(
      eventId: $eventId
      rankInput: $rankInput
      scoreUpdatedAt: $scoreUpdatedAt
    ) {
      ... on Event {
        id
        status
      }
      ... on GraphqlRequestError {
        message
        code
      }
    }
  }
`

type PlanEvent = Pick<
  Event,
  'id' | 'gameFormat' | 'sport' | 'scoreUpdatedAt' | 'competitors' | 'matches'
>

interface EventPlanPanelProps {
  event: PlanEvent
  /** Whether scores can be edited and the event completed */
  editable: boolean
  /** Called after any score or status change, to refetch the event */
  onChanged: () => void
  onCompleted: () => void
}

type ScoreEditor = {
  match: Match
  setNumber: number
  initialScore?: SetScore
  canDelete: boolean
}

const sideLabel = (side: Match['side1']) =>
  side.players.map((p) => p.name ?? '-').join(' / ')

function MatchSidePlayers({
  side,
  align,
}: {
  side: Match['side1']
  align: 'left' | 'right'
}) {
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: align === 'left' ? 'flex-start' : 'flex-end',
        gap: 0.5,
      }}
    >
      {groupConsecutiveByKey(side.players, (p) => p.competitorIndex).map((team) => (
        <Box
          key={team[0].id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexDirection: align === 'left' ? 'row' : 'row-reverse',
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {team[0].competitorIndex}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {team.map((p) => (
              <UserAvatar key={p.id} user={p} showMobile={false} />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default function EventPlanPanel({
  event,
  editable,
  onChanged,
  onCompleted,
}: EventPlanPanelProps) {
  const [currentStage, setCurrentStage] = useState(1)
  const [editor, setEditor] = useState<ScoreEditor | null>(null)
  const [updateEventScore] = useMutation(UpdateEventScoreDocument)
  const [completeEvent, { loading: completing }] = useMutation(CompleteEventDocument)

  const planner = EventPlanner.getPlanner(event)
  const { roundsPerStage, matchesPerRound, numberStages } = planner.setting
  // Best-of-N-sets scoring only applies to single-match formats; round-robin
  // formats use single-set scoring per match.
  const multiSetsEnabled = planner.totalMatchCount === 1
  // The latest stage the event has recorded scores for
  const eventStage = Math.ceil(event.matches.length / matchesPerRound / roundsPerStage)
  const allScored = event.matches.length === planner.totalMatchCount

  const toSeedings = (side: MatchSide) => (Array.isArray(side) ? side : [side])
  const eventPlan = planner.plan.map((stagePlan, stageIdx) =>
    stagePlan.map((roundPlan, roundIdx) =>
      roundPlan.map(([side1, side2]) =>
        buildMatch(
          stageIdx * roundsPerStage + roundIdx + 1,
          toSeedings(side1),
          toSeedings(side2),
          event,
        ),
      ),
    ),
  )

  const saveScore = async (match: Match, setNumber: number, score: SetScore | null) => {
    try {
      await updateEventScore({
        variables: {
          score: {
            setNumber,
            side1Score: score?.side1Score ?? null,
            side2Score: score?.side2Score ?? null,
          },
          ...(match.matchId
            ? { matchId: match.matchId }
            : {
                newMatch: {
                  eventId: event.id,
                  round: match.round,
                  players: [
                    ...match.side1.players.map((p) => ({ userId: p.id, side: 1 })),
                    ...match.side2.players.map((p) => ({ userId: p.id, side: 2 })),
                  ],
                },
              }),
        },
      })
      setEditor(null)
      onChanged()
    } catch (err) {
      message.error(`保存比分失败: ${err instanceof Error ? err.message : err}`)
    }
  }

  const openEditor = (match: Match, setIndex: number) => {
    const matchStage = Math.ceil(match.round / roundsPerStage)
    if (matchStage < eventStage) {
      message.error(`第${eventStage}阶段已开始，不能修改第${matchStage}阶段比分`)
      return
    }
    const set = match.sets[setIndex]
    setEditor({
      match,
      setNumber: setIndex + 1,
      initialScore: set && { side1Score: set.side1Score, side2Score: set.side2Score },
      canDelete:
        multiSetsEnabled && match.sets.length > 1 && setIndex === match.sets.length - 1,
    })
  }

  const doComplete = async () => {
    const { scoreUpdatedAt } = event
    if (!scoreUpdatedAt) return
    const ranks = planner.getStageRankList(numberStages)
    const seededPlayers = expandCompetitors(event.competitors)
    const { data } = await completeEvent({
      variables: {
        eventId: event.id,
        scoreUpdatedAt,
        rankInput: ranks.map((seeding, idx) => ({
          playerUserId: seededPlayers[seeding - 1].player.id as string,
          rank: idx + 1,
        })),
      },
    })
    if (data?.completeEvent.__typename === 'GraphqlRequestError') {
      if (data.completeEvent.code === RequestErrorCode.EventScoreStale) {
        message.error('其他用户修改了比分，当前比分已刷新，请确认后再结束比赛')
        onChanged()
      } else {
        message.error(data.completeEvent.message ?? '操作失败')
      }
      return
    }
    message.success('比赛已结束')
    onCompleted()
  }

  const handleComplete = () => {
    message.prompt('结束比赛后将按当前比分计算并记录最终名次，比分将无法再修改。', {
      title: '确认结束比赛',
      noLabel: '返回',
      yesLabel: '结束比赛',
      onYesButton: doComplete,
    })
  }

  return (
    <Box>
      {numberStages > 1 && (
        <Tabs
          value={currentStage}
          onChange={(_, v: number) => setCurrentStage(v)}
          sx={{ mb: 1 }}
        >
          {Array.from({ length: numberStages }, (_, i) => i + 1).map((i) => (
            <Tab key={i} value={i} label={`第${i}阶段`} />
          ))}
        </Tabs>
      )}

      {currentStage > eventPlan.length ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          上一阶段比赛尚未结束，第{currentStage}阶段暂未开始
        </Typography>
      ) : (
        eventPlan[currentStage - 1].flat().map((match) => {
          const seedKey = (side: Match['side1']) =>
            side.players.map((p) => p.seeding).join('-')
          return (
            <Box
              key={`${match.round}-${seedKey(match.side1)}-${seedKey(match.side2)}`}
              sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1.5, py: 1, mb: 1 }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                第 {match.round} 轮
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MatchSidePlayers side={match.side1} align="left" />
                <Box sx={{ textAlign: 'center', minWidth: 96 }}>
                  {match.sets.length === 0 ? (
                    editable ? (
                      <Button size="small" onClick={() => openEditor(match, 0)}>
                        录入比分
                      </Button>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        —
                      </Typography>
                    )
                  ) : (
                    match.sets.map((s, i) => {
                      const side1Won = s.side1Score > s.side2Score
                      return (
                        <Box
                          // biome-ignore lint/suspicious/noArrayIndexKey: no set id
                          key={i}
                          component={editable ? 'button' : 'div'}
                          onClick={editable ? () => openEditor(match, i) : undefined}
                          sx={{
                            display: 'block',
                            width: '100%',
                            border: 0,
                            background: 'none',
                            font: 'inherit',
                            cursor: editable ? 'pointer' : 'default',
                            borderRadius: 0.5,
                            '&:hover': editable ? { bgcolor: 'action.selected' } : {},
                          }}
                        >
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{ fontWeight: 'bold' }}
                          >
                            <Box
                              component="span"
                              sx={{
                                color: side1Won ? 'success.main' : 'text.secondary',
                              }}
                            >
                              {s.side1Score}
                            </Box>
                            {' : '}
                            <Box
                              component="span"
                              sx={{
                                color: side1Won ? 'text.secondary' : 'success.main',
                              }}
                            >
                              {s.side2Score}
                            </Box>
                          </Typography>
                          {match.sets.length > 1 && (
                            <Typography
                              variant="caption"
                              component="span"
                              sx={{ color: 'text.secondary', ml: 1 }}
                            >
                              第{i + 1}盘
                            </Typography>
                          )}
                        </Box>
                      )
                    })
                  )}
                  {editable && multiSetsEnabled && match.sets.length > 0 && (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => openEditor(match, match.sets.length)}
                    >
                      添加新盘
                    </Button>
                  )}
                </Box>
                <MatchSidePlayers side={match.side2} align="right" />
              </Box>
            </Box>
          )
        })
      )}

      {editable && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            variant="contained"
            disabled={!allScored || completing}
            onClick={handleComplete}
          >
            结束比赛
          </Button>
        </Box>
      )}

      {editor && (
        <SetScoreDialog
          key={`${editor.match.round}-${editor.setNumber}`}
          setNumber={editor.setNumber}
          initialScore={editor.initialScore}
          sport={event.sport}
          side1Label={sideLabel(editor.match.side1)}
          side2Label={sideLabel(editor.match.side2)}
          onClose={() => setEditor(null)}
          onConfirm={(score) => saveScore(editor.match, editor.setNumber, score)}
          onDelete={
            editor.canDelete
              ? () =>
                  message.prompt(`确定要删除第${editor.setNumber}盘比分吗？`, {
                    title: '确认删除',
                    noLabel: '返回',
                    yesLabel: '删除',
                    onYesButton: () => saveScore(editor.match, editor.setNumber, null),
                  })
              : undefined
          }
        />
      )}
    </Box>
  )
}
